import isEmpty from 'lodash/isEmpty';
import reduce from 'lodash/reduce';
import indexOf from 'lodash/indexOf';
import map from 'lodash/map';
import size from 'lodash/size';
import get from 'lodash/get';
import forEach from 'lodash/forEach';
import find from 'lodash/find';
import filter from 'lodash/filter';
import includes from 'lodash/includes';
import concat from 'lodash/concat';
import toString from 'lodash/toString';
import {
    ACTIVE_SCREEN_KEY,
    setScreenData,
    removeScreenData,
    formatMessage,
    setErrorScreenMessage,
    removeAllScreenMessages,
    setSuccessfulScreenMessage,
    setWarningScreenMessage,
    CwLog,
    hideDialog,
    showDialog,
    getScreenData,
    setModuleCache,
    getScreenUpdated,
    Store,
} from 'cw-ui-core';
import { DEFAULT_LOADING_DIALOG } from 'src/components/DefaultLoadingDialog/DefaultLoadingDialog';
import { all, call, put, delay } from 'redux-saga/effects';
import {
    GET,
    POST,
    DELETE,
    PATCH,
    PUT,
    getHeaders,
    getPartnerId,
    autoHideMessages,
    autoHideMessagesAfter,
    convertMapToPatches,
    getOriginPartnerId,
} from 'src/services/ApiService';
import { ALERT_TYPE } from 'src/asio-neon-components/Constants';
import setAlertScreenMessage from 'src/asio-neon-components/Utils';
import {
    getUserContactInfoFromClaims,
    updateContactInfoClaim,
    createAdditionalClaims,
    getAdditionalClaimsToRemove,
    initAddressFields,
} from 'src/services/ContactClaimService';
import { ADD_USER_DRAWER, EDIT_USER_DRAWER, HTTP_STATUS_OK } from 'src/features/users/constants/drawerConstants';
import Locale from 'src/i18n';
import {
    fromJS,
    // isImmutable
} from 'immutable';
import {
    DEFAULT_ROLES,
    DUO_MFA,
    LOCAL_LOGIN,
    DUO_MFA_DETAILS,
    MFA_DETAILS,
    ISDUO_USERNAME_SAME_AS_LOGIN_EMAIL,
} from 'src/constants';
import { UMS_LOADING_DIALOG } from 'src/features/users/components/UMSLoadingDialog/UMSLoadingDialog';
import { UPDATE_MFA_DIALOG_CONSTANTS } from 'src/features/users/constants/constants';
import { toArray, formatErrorMessage } from 'src/util/Util';
import { getAppDisplayName } from 'src/features/users/utils/ConnectedAppUtils';
import {
    createDuoAdditionalClaims,
    duoClaimsToRemove,
    updateDuoClaims,
    duoMfaDetails,
} from 'src/services/DuoMfaClaimService';

export const defaultPageSize = 50;

const getDisplayErrorText = (errorString, appName) => {
    return `${errorString} for ${appName}`;
};

export const searchList = (list, text) => {
    return list.filter(
        u =>
            `${u.firstName} ${u.lastName}`.toLocaleLowerCase().indexOf(text.toLocaleLowerCase()) > -1 ||
            u.email.toLocaleLowerCase().indexOf(text.toLocaleLowerCase()) > -1
    );
};

export function* UMSLoader() {
    yield delay(100);
    if (!Store().getState().dialog.get(UMS_LOADING_DIALOG)) {
        yield put(showDialog(UMS_LOADING_DIALOG));
    }
}

export function* runSagaWithUMSLoader(saga, ...params) {
    yield call(UMSLoader);
    const response = yield call(saga, ...params);

    if (Store().getState().dialog.get(UMS_LOADING_DIALOG)) {
        yield put(hideDialog(UMS_LOADING_DIALOG));
    }
    return response;
}

export function* DefaultLoader() {
    yield delay(100);
    if (!Store().getState().dialog.get(DEFAULT_LOADING_DIALOG)) {
        yield put(showDialog(DEFAULT_LOADING_DIALOG));
    }
}

export function* runSagaWithDefaultLoader(saga, ...params) {
    yield call(DefaultLoader);
    const response = yield call(saga, ...params);

    if (Store().getState().dialog.get(DEFAULT_LOADING_DIALOG)) {
        yield put(hideDialog(DEFAULT_LOADING_DIALOG));
    }
    return response;
}

export const getPaginationParams = () => ({
    currentPageIndex: getScreenData(Store().getState().screen, ['currentPageIndex']),
    totalCount: getScreenData(Store().getState().screen, ['totalCount']),
    pageCount: getScreenData(Store().getState().screen, ['pageCount']),
    pageSize: getScreenData(Store().getState().screen, ['pageSize']),
});

export const getPaginationData = (users, searchText = null, pageindex = 0) => {
    const filteredUsers = searchText ? searchList(users, searchText) : users;
    const totalCount = filteredUsers.length;
    const currentPageIndex = pageindex || 0;
    const pageCount = Math.ceil(totalCount / defaultPageSize);
    const arraySpliceIndex = currentPageIndex === 0 ? 0 : currentPageIndex * 49 + 1;
    const filteredUsersToShow = filteredUsers.splice(arraySpliceIndex, 50);
    Store().dispatch(setScreenData(['totalCount'], totalCount));
    Store().dispatch(setScreenData(['currentPageIndex'], currentPageIndex));
    Store().dispatch(setScreenData(['pageCount'], pageCount));
    Store().dispatch(setScreenData(['filteredUsersToShow'], filteredUsersToShow));
};

// Load all users from API and set screen data
export function* loadAllUsers(screenKey = ACTIVE_SCREEN_KEY) {
    yield put(setScreenData(['isAllUsersReady'], false));
    const version = '3.0';
    const result = yield call(GET, '/users', getHeaders(version));
    yield put(setScreenData(['autoResetSelectedRows'], true));
    if (result.isSuccess) {
        const featureResult = result.response.data;
        const sortedList = featureResult ?? [];
        yield put(setScreenData(['allUsers'], sortedList, screenKey));
        getPaginationData(sortedList);
        yield put(setScreenData(['isAllUsersReady'], true));
    } else {
        const error = formatMessage(Locale.failedGetUsers);
        yield put(setErrorScreenMessage(error, false, result.errorMessages, screenKey));
        yield put(setScreenData(['isAllUsersReady'], true));
    }
    yield put(setScreenData(['autoResetSelectedRows'], false));
}

export const getFilters = filters => {
    const { rolesFilter, statusFilter, authenticationFilter, mfaFilter, connectedAppsFilter } = filters;
    const filterReducer = (accumulator, currentValue) => `${accumulator},${currentValue}`;
    const filterQueryArray = [];

    if (!isEmpty(rolesFilter)) {
        const rolesFilterParams = `identityRoleIds=${reduce(rolesFilter, filterReducer)}`;
        filterQueryArray.push(rolesFilterParams);
    }
    if (!isEmpty(statusFilter)) {
        const statusFilterParams = `status=${reduce(statusFilter, filterReducer)}`;
        filterQueryArray.push(statusFilterParams);
    }
    if (!isEmpty(authenticationFilter)) {
        authenticationFilter[indexOf(authenticationFilter, 'Local Login')] = null;
        const authFilterParams = `loginScheme=${reduce(authenticationFilter, filterReducer)}`;
        filterQueryArray.push(authFilterParams);
    }
    if (!isEmpty(mfaFilter)) {
        const mfaFilterParams = `mfaProviders=${reduce(mfaFilter, filterReducer)}&loginScheme=null`;
        filterQueryArray.push(mfaFilterParams);
    }
    if (!isEmpty(connectedAppsFilter)) {
        const appsFilterParams = `connectedAppIds=${reduce(connectedAppsFilter, filterReducer)}`;
        filterQueryArray.push(appsFilterParams);
    }
    const filterQuery = filterQueryArray.join('&');
    return filterQuery;
};

// get all users with pagination
export function* loadUsers(pageIndex = 1, pageSize = 50, searchText, filters, screenKey = ACTIVE_SCREEN_KEY) {
    yield put(setScreenData(['isReady'], false, screenKey));
    yield put(setScreenData(['isGuestUsersReady'], false, screenKey));
    yield put(setScreenData(['isUsersFailedToLoad'], false, screenKey));
    // yield put(showDialog(DEFAULT_LOADING_DIALOG));
    let url = `/users`;
    const query = [];

    if (pageIndex || pageSize) {
        const pageQuery = `page=${pageIndex}&pageSize=${pageSize}`;
        query.push(pageQuery);
    }
    if (!isEmpty(searchText)) {
        const searchQuery = `filter=firstName co "${searchText}" or lastName co "${searchText}" or email co "${searchText}"`;
        query.push(searchQuery);
    }

    if (!isEmpty(filters)) {
        const filterQuery = getFilters(filters);
        query.push(filterQuery);
    }
    url = query ? `${url}?${query.join('&')}` : url;

    const version = '3.0';
    const result = yield call(GET, url, getHeaders(version));
    yield put(setScreenData(['autoResetSelectedRows'], true, screenKey));
    if (result.isSuccess) {
        const featureResult = result.response.data;
        const sortedList = (featureResult || []).sort((a, b) =>
            `${a.firstName} ${a.lastName}`.localeCompare(`${b.firstName} ${b.lastName}`)
        );
        yield put(setScreenData(['users'], sortedList, screenKey));

        const usersPaging = result.headers.get('Link').split(',');
        const pagingData = {};
        usersPaging.forEach(p => {
            const pageData = p.split(';');
            const pageHint = pageData[1].split('=');
            const objKey = pageHint[1].substring(1, pageHint[1].length - 1);
            const objValue = pageData[0].substring(1, pageData[0].length - 1);
            pagingData[objKey] = objValue;
        });
        const lastPageUrl = new URL(pagingData.last);
        const lastPageCount = parseInt(lastPageUrl.searchParams.get('page'), 10);
        const totalCount = result.response.totalItems;
        const currentPageIndex = pageIndex - 1;

        yield put(setScreenData(['currentPageIndex'], currentPageIndex, screenKey));
        yield put(setScreenData(['totalCount'], totalCount, screenKey));
        yield put(setScreenData(['pageCount'], lastPageCount, screenKey));
        yield put(setScreenData(['pageSize'], pageSize, screenKey));
        yield put(setScreenData(['isReady'], true, screenKey));
        yield put(setScreenData(['isGuestUsersReady'], true, screenKey));
    } else {
        const error = formatMessage(Locale.failedGetUsers);
        yield put(setErrorScreenMessage(error, false, result.errorMessages));
        yield put(setScreenData(['isReady'], true, screenKey));
        yield put(setScreenData(['isGuestUsersReady'], true, screenKey));
        yield put(setScreenData(['isUsersFailedToLoad'], true, screenKey));
    }
    yield put(setScreenData(['autoResetSelectedRows'], false, screenKey));
    // yield put(hideDialog(DEFAULT_LOADING_DIALOG));
}

// get users with connected apps
export function* loadUsersWithConnectedApps(
    pageIndex = 1,
    pageSize = 50,
    searchText = '',
    filters = [],
    screenKey = ACTIVE_SCREEN_KEY,
    keepRowsSelected = false,
    originAccount = false
) {
    // const queryParamsFromStore = getScreenData(Store().getState().screen, ['loadUsers', 'queryParams'], screenKey);
    // const queryParamsInProgress =
    //     queryParamsFromStore && isImmutable(queryParamsFromStore) ? queryParamsFromStore.toJS() : {};
    // const isAnotherRequestInProgress = !getScreenData(
    //     Store().getState().screen,
    //     originAccount ? ['isGuestUsersReady'] : ['isReady'],
    //     screenKey
    // );

    // if (
    //     isAnotherRequestInProgress &&
    //     get(queryParamsInProgress, 'pageIndex') === pageIndex &&
    //     get(queryParamsInProgress, 'pageSize') === pageSize &&
    //     get(queryParamsInProgress, 'searchText', '') === searchText &&
    //     isEqual(get(queryParamsInProgress, 'filters', []), filters)
    // ) {
    //     return;
    // }

    yield put(setScreenData(originAccount ? ['isGuestUsersReady'] : ['isReady'], false, screenKey));
    yield put(setScreenData(['isUsersFailedToLoad'], false, screenKey));
    const queryParams = {
        pageIndex,
        pageSize,
        searchText,
        filters,
    };
    yield put(setScreenData(['loadUsers', 'queryParams'], queryParams, screenKey));
    let url = '/iambff/v1/users';
    const query = [];

    if (pageIndex || pageSize) {
        const searchQuery = `page=${pageIndex}&pageSize=${pageSize}`;
        query.push(searchQuery);
    }

    if (!isEmpty(searchText)) {
        const searchQuery = `search=${encodeURIComponent(searchText)}`;
        query.push(searchQuery);
    }

    if (!isEmpty(filters)) {
        const filterQuery = getFilters(filters);
        query.push(filterQuery);
    }

    query.push(`originAccount=${originAccount}`);

    url = query ? `${url}?${query.join('&')}` : url;
    const result = yield call(GET, url, getHeaders());
    if (!keepRowsSelected) yield put(setScreenData(['autoResetSelectedRows'], true, screenKey));
    if (result.isSuccess) {
        const sortedList = (result.response.data || []).sort((a, b) =>
            `${a.firstName} ${a.lastName}`.localeCompare(`${b.firstName} ${b.lastName}`)
        );
        yield put(setScreenData(['usersWithConnectedApps'], sortedList, screenKey));

        const usersPaging = result.headers.get('Link') ? result.headers.get('Link').split(',') : [];
        const pagingData = {};
        usersPaging.forEach(p => {
            const pageData = p.split(';');
            const pageHint = pageData[1].split('=');
            const objKey = pageHint[1].substring(1, pageHint[1].length - 1);
            const objValue = pageData[0].substring(1, pageData[0].length - 1);
            pagingData[objKey] = objValue;
        });
        const lastPageUrlSearchParams = new URLSearchParams(pagingData.last);
        const lastPageCount = parseInt(lastPageUrlSearchParams.get('page'), 10);
        const totalCount = result.response.totalItems;
        const currentPageIndex = pageIndex - 1;

        yield put(setScreenData(['currentPageIndex'], currentPageIndex, screenKey));
        yield put(setScreenData(['totalCount'], totalCount, screenKey));
        yield put(setScreenData(['pageCount'], lastPageCount, screenKey));
        yield put(setScreenData(['pageSize'], pageSize, screenKey));
        yield put(setScreenData(originAccount ? ['isGuestUsersReady'] : ['isReady'], true, screenKey));
    } else {
        const error = formatMessage(Locale.failedGetUsers);
        yield put(setErrorScreenMessage(error, false, result.errorMessages));
        yield put(setScreenData(originAccount ? ['isGuestUsersReady'] : ['isReady'], true, screenKey));
        yield put(setScreenData(['isUsersFailedToLoad'], true, screenKey));
    }
    if (!keepRowsSelected) yield put(setScreenData(['autoResetSelectedRows'], false, screenKey));
}

export function* loadAllUsersWithConnectedApps(originAccount = false, screenKey = ACTIVE_SCREEN_KEY) {
    let url = '/iambff/v1/users';
    if (originAccount) {
        url = `${url}?originAccount=true`;
    }
    const result = yield call(GET, url, getHeaders());
    if (result.isSuccess) {
        const sortedList = (result.response.data || []).sort((a, b) =>
            `${a.firstName} ${a.lastName}`.localeCompare(`${b.firstName} ${b.lastName}`)
        );
        yield put(setScreenData(['allUsersWithConnectedApps'], sortedList, screenKey));
    } else {
        const error = formatMessage(Locale.failedGetUsers);
        yield put(setErrorScreenMessage(error, false, result.errorMessages));
    }
}

export function* filterUsers(screenKey = ACTIVE_SCREEN_KEY, originAccount = false) {
    const text = getScreenData(Store().getState().screen, ['usersSearchText'], screenKey);

    const filters = toArray(getScreenData(Store().getState().screen, ['addFilter'], screenKey));
    yield call(loadUsersWithConnectedApps, 1, 50, text, filters, screenKey, undefined, originAccount);
    yield put(setScreenData(['isFirstTime'], false, screenKey));
}

export function* SearchForNewUsersAuth(screenKey = ACTIVE_SCREEN_KEY) {
    const searchText = getScreenData(Store().getState().screen, ['usersSearchText'], screenKey);
    yield call(loadUsersWithConnectedApps, null, null, searchText, null, screenKey);
}

export function* reloadCurrentUsersPage(
    screenKey = ACTIVE_SCREEN_KEY,
    keepRowsSelected = false,
    originAccount = false
) {
    const searchText = getScreenData(Store().getState().screen, ['usersSearchText']);
    const { currentPageIndex = 0, pageSize = 50 } = getPaginationParams();

    const filters = toArray(getScreenData(Store().getState().screen, ['addFilter'], screenKey));

    yield call(
        runSagaWithUMSLoader,
        loadUsersWithConnectedApps,
        currentPageIndex + 1,
        pageSize,
        searchText,
        filters,
        screenKey,
        keepRowsSelected,
        originAccount
    );
}

export function* deleteUser(id) {
    const result = yield call(DELETE, `/users/${id}`, getHeaders('1.0'));
    if (result.isSuccess) {
        yield call(reloadCurrentUsersPage);
        yield put(setSuccessfulScreenMessage(formatMessage(Locale.userDeleted), false, []));
        yield call(autoHideMessages);
    } else {
        const error = formatMessage(Locale.failedDeleteUser);
        yield put(setErrorScreenMessage(error, false, result.errorMessages));
        yield call(autoHideMessages);
    }
}

export function* deleteUsers(ids, screenKey = ACTIVE_SCREEN_KEY) {
    if (!isEmpty(ids)) {
        const deleteUserCalls = map(ids, id => call(DELETE, `/users/${id}`, getHeaders('1.0')));
        const deleteUserResults = yield all(deleteUserCalls);

        if (!isEmpty(deleteUserResults)) {
            yield call(reloadCurrentUsersPage);
            for (let i = 0; i < size(deleteUserResults); i += 1) {
                const result = deleteUserResults[i];
                if (result.isSuccess) {
                    yield put(setSuccessfulScreenMessage(formatMessage(Locale.userDeleted), false, []));
                    yield put(
                        setAlertScreenMessage(
                            ALERT_TYPE.success,
                            null,
                            formatMessage(Locale.userDeleted),
                            true,
                            screenKey
                        )
                    );
                    yield call(autoHideMessages);
                } else {
                    const error = formatMessage(Locale.failedDeleteUser);
                    yield put(setErrorScreenMessage(error, false, result.errorMessages));
                    setAlertScreenMessage(ALERT_TYPE.error, null, result.errorMessages, true, screenKey);

                    yield call(autoHideMessages);
                }
            }
        }
    }
}

export function* bulkDeleteUsers(ids) {
    const body = { userIds: ids };
    const result = yield call(DELETE, `/users/BulkDeleteUsers`, getHeaders('3.0'), body);
    if (result.isSuccess) {
        yield call(reloadCurrentUsersPage);
        yield put(setSuccessfulScreenMessage(formatMessage(Locale.userDeleted), false, []));
        yield call(autoHideMessages);
    } else {
        yield put(hideDialog(UMS_LOADING_DIALOG));
        const error = formatMessage(Locale.failedDeleteUser);
        yield put(setErrorScreenMessage(error, false, result.errorMessages));
        yield call(autoHideMessages);
    }
}

export function* bulkUpdateAuthMethod(ids, scheme, screenKey = ACTIVE_SCREEN_KEY) {
    const body = {
        userIds: ids,
        scheme,
    };
    const result = yield call(PUT, `/users/BulkUpdateAuthMethod`, getHeaders('3.0'), body);
    if (result.isSuccess) {
        yield call(reloadCurrentUsersPage);
        yield put(setSuccessfulScreenMessage(formatMessage(Locale.authenticationMethodAddedSuccessful), false, []));
        yield put(
            setAlertScreenMessage(
                ALERT_TYPE.success,
                null,
                formatMessage(Locale.authenticationMethodAddedSuccessful),
                true,
                screenKey
            )
        );
        yield call(autoHideMessages);
    } else {
        const error = formatMessage(Locale.authenticationMethodAddedFailed);
        yield put(setErrorScreenMessage(error, false, result.errorMessages));
        yield put(setAlertScreenMessage(ALERT_TYPE.error, null, error, true, screenKey));
        yield call(autoHideMessages);
    }
}

function* handleUpdateMfaResponse(result) {
    if (result.isSuccess) {
        const failedUsers = get(result, 'response.failedUsers');
        const totalCount = get(result, 'response.totalCount');
        const successCount = get(result, 'response.successCount');
        const responseErrorMessage = get(result, 'response.message') || '';
        yield call(reloadCurrentUsersPage);
        if (!isEmpty(failedUsers) && successCount > 0) {
            const partialSuccessMessage = formatMessage(Locale.partialSuccessBulkUpdateMfa, {
                userCount: size(failedUsers),
            });
            yield put(setWarningScreenMessage(partialSuccessMessage.concat(' ', responseErrorMessage), false, []));
        } else if (!isEmpty(failedUsers) && successCount === 0) {
            const errorMessage = `${formatMessage(Locale.errorBulkUpdateMfa)} ${responseErrorMessage}`;
            yield put(setErrorScreenMessage(errorMessage, false, []));
        } else {
            const successMessage = formatMessage(Locale.successBulkUpdateMfa, {
                userCount: totalCount,
            });
            yield put(setSuccessfulScreenMessage(successMessage, false, []));
        }
        yield call(autoHideMessages);
    } else {
        const errorMessage = formatMessage(Locale.errorBulkUpdateMfa);
        yield put(setErrorScreenMessage(errorMessage, false, result.errorMessages));
        yield call(autoHideMessages);
    }
}

export function* bulkUpdateMfa(ids, mfaProvidersData) {
    const isAddMfaSelected = get(mfaProvidersData, `${UPDATE_MFA_DIALOG_CONSTANTS.ADD_METHOD_FLAG}.isSelected`, false);
    const isRemoveMfaSelected = get(
        mfaProvidersData,
        `${UPDATE_MFA_DIALOG_CONSTANTS.REMOVE_METHOD_FLAG}.isSelected`,
        false
    );

    let addMfaResult;
    if (isAddMfaSelected) {
        const body = {
            userIds: ids,
            mfaProviders: get(mfaProvidersData, `${UPDATE_MFA_DIALOG_CONSTANTS.ADD_METHOD_FLAG}.value`, []),
        };
        addMfaResult = yield call(PUT, `/users/admin/mfa/BulkAddMfaProviders`, getHeaders('3.0'), body);
        yield call(handleUpdateMfaResponse, addMfaResult);
    }

    if (
        (isAddMfaSelected && get(addMfaResult, 'isSuccess', false) && isRemoveMfaSelected) ||
        (!isAddMfaSelected && isRemoveMfaSelected)
    ) {
        const body = {
            userIds: ids,
            mfaProviders: get(mfaProvidersData, `${UPDATE_MFA_DIALOG_CONSTANTS.REMOVE_METHOD_FLAG}.value`, []),
        };
        const removeMfaResult = yield call(PUT, `/users/admin/mfa/BulkRemoveMfaProviders`, getHeaders('3.0'), body);
        yield call(handleUpdateMfaResponse, removeMfaResult);
    }
}

function* getUserInfo(id, screenKey = ACTIVE_SCREEN_KEY) {
    yield put(setScreenData(['isUserAccessedProductsReady'], false));

    const url = `/users/${id}`;
    const version = '3.0';
    const result = yield call(GET, url, getHeaders(version));

    if (!result.isSuccess) {
        const error = formatMessage(Locale.failedGetUsers);
        yield put(setErrorScreenMessage(error, false, result.errorMessages));
        yield put(setScreenData(['isReady'], true));
        yield put(setScreenData(['isGuestUsersReady'], true));
        return;
    }

    const userDetails = result.response;
    userDetails.contactInfo = getUserContactInfoFromClaims(userDetails.additionalClaims);
    userDetails.isContactEmailSameAsHome = userDetails.email === userDetails.contactInfo.contactEmail;
    if (!userDetails.contactInfo.addressInfo) userDetails.contactInfo.addressInfo = initAddressFields();
    if (userDetails.loginScheme === null) {
        userDetails.loginScheme = LOCAL_LOGIN;
        userDetails.currentLoginScheme = LOCAL_LOGIN;
    } else {
        userDetails.currentLoginScheme = userDetails.loginScheme;
    }
    const duoMfa = duoMfaDetails(userDetails?.additionalClaims);
    if (duoMfa?.duoEnabled === 'enabled' && !isEmpty(duoMfa?.duoUserName) && !isEmpty(duoMfa?.provider)) {
        yield put(setScreenData([MFA_DETAILS, DUO_MFA], true, screenKey));
        yield put(setScreenData([DUO_MFA_DETAILS], duoMfa, screenKey));
    }
    if (!isEmpty(duoMfa?.duoUserName) && duoMfa?.duoUserName !== userDetails?.email) {
        yield put(setScreenData([DUO_MFA_DETAILS, ISDUO_USERNAME_SAME_AS_LOGIN_EMAIL], false, screenKey));
    } else {
        yield put(setScreenData([DUO_MFA_DETAILS, ISDUO_USERNAME_SAME_AS_LOGIN_EMAIL], true, screenKey));
        yield put(setScreenData([DUO_MFA_DETAILS, 'duoUserName'], userDetails?.email, screenKey));
    }

    yield put(setScreenData(['userDetails'], userDetails, screenKey));

    if (
        !userDetails?.contactInfo?.contactId &&
        userDetails.firstName?.length > 0 &&
        userDetails.lastName?.length > 0 &&
        userDetails.contactInfo.contactEmail?.length > 0
    ) {
        const query = `email=${encodeURIComponent(userDetails.contactInfo.contactEmail)}`;
        const resultContactMatches = yield call(GET, `/contact/matches?${query}`, getHeaders(3.0));

        if (resultContactMatches.isSuccess) {
            yield put(setScreenData(['userContactMatches'], resultContactMatches.response, screenKey));
        } else {
            CwLog.error(resultContactMatches);
        }
    }
}

export function* getUserAssignedRoles(id, screenKey = ACTIVE_SCREEN_KEY) {
    const resultRoles = yield call(GET, `/users/${id}/roles`, getHeaders());
    if (resultRoles.isSuccess) {
        let roles = [];
        if (resultRoles.response) {
            roles = map(resultRoles.response, role => role.id);
        }
        yield put(setScreenData(['checkboxes', 'selectedRoles'], roles, screenKey));
    } else {
        const error = formatMessage(Locale.failedGetUserRoles);
        yield put(setErrorScreenMessage(error, false, resultRoles.errorMessages, screenKey));
        yield call(autoHideMessages);
    }
    const resultProducts = yield call(GET, `/clients/users/${id}/clients`, getHeaders());
    if (resultProducts.isSuccess) {
        const accessedProducts = resultProducts.response;

        yield put(setScreenData(['checkboxes', 'selectedProducts'], accessedProducts, screenKey));
        yield put(setScreenData(['isUserAccessedProductsReady'], true));
    } else {
        const error = formatMessage(Locale.failedGetUserProducts);
        yield put(setErrorScreenMessage(error, false, resultProducts.errorMessages, EDIT_USER_DRAWER));
        yield call(autoHideMessages, EDIT_USER_DRAWER);
        yield put(setScreenData(['isUserAccessedProductsReady'], true));
    }
}

export function* getUser(id, screenKey = ACTIVE_SCREEN_KEY) {
    yield all([call(getUserInfo, id, screenKey), call(getUserAssignedRoles, id, screenKey)]);
}

export function* getHomeAppRoleAssignments(userId) {
    yield put(setScreenData(['isAppRoleAssignmentsReady'], false));

    const resultRoles = yield call(GET, `/users/${userId}/roles`, getHeaders());

    const identityRoleIds = !isEmpty(resultRoles.response) ? map(resultRoles.response, ({ id }) => id) : [];
    let url = `/iambff/v1/users/${userId}/app-role-mappings?&app_ids=cwhome_code`;

    if (!isEmpty(identityRoleIds)) {
        url = `${url}&identity_role_ids=${toString(identityRoleIds)}`;
    }

    const result = yield call(GET, url, getHeaders());

    if (result.response && !isEmpty(result.response.connectedApps)) {
        const cwHomeApp = find(result.response.connectedApps, ({ connectedAppId }) => connectedAppId === 'cwhome_code');
        if (cwHomeApp) {
            let appRoles = [];
            forEach(cwHomeApp.identityRoles, identityRole => {
                const appRoleIds = map(identityRole.applicationRoles, ({ roleId }) => roleId);
                if (!isEmpty(appRoleIds)) {
                    appRoles = concat(appRoles, appRoleIds);
                }
            });

            if (!isEmpty(cwHomeApp.applicationRoles)) {
                const appRoleIds = map(cwHomeApp.applicationRoles, ({ roleId }) => roleId);
                if (!isEmpty(appRoleIds)) {
                    appRoles = concat(appRoles, appRoleIds);
                }
            }

            const uniqueAppRoles = [...new Set(appRoles)];
            yield put(setModuleCache(['homeAppRoles'], uniqueAppRoles));
        }
    }

    yield put(setModuleCache(['isAppRoleAssignmentsReady'], true));
}
function* updateUserIdentityRolesBff(id, roles) {
    const url = `/iambff/v1/users/${id}/identity-roles`;
    const payload = { identityRoleIds: roles };
    const result = yield call(PUT, url, getHeaders(), payload);
    return result;
}

function* updateUserProductsRoles(id, products, roles, screenKey = ADD_USER_DRAWER) {
    let rolesResultBff;
    let productsResult;
    const errors = [];
    const failedIdentityRoleAssignments = [];
    const isRolesChanged = getScreenUpdated(
        Store().getState().screen,
        ['assignedRoles', 'selectedRoles'],
        EDIT_USER_DRAWER
    );
    if (isRolesChanged != undefined || roles.length > 0) {
        rolesResultBff = yield call(updateUserIdentityRolesBff, id, roles);
        if (rolesResultBff && rolesResultBff.isSuccess) {
            const rolesResponseDescription = get(rolesResultBff, ['response', 'description', 'responses'], []);
            if (rolesResponseDescription.length >= 0) {
                rolesResponseDescription.forEach(identityRole => {
                    if (identityRole.Status !== HTTP_STATUS_OK) {
                        failedIdentityRoleAssignments.push({
                            error: true,
                            message: identityRole.message,
                            identityRoleId: identityRole.identityRoleId,
                        });
                    }
                });
            }
        }
        if (rolesResultBff && !rolesResultBff.isSuccess) {
            yield put(setScreenData(['isIdentityRoleFailure'], true, screenKey));
        }
        if (!isEmpty(failedIdentityRoleAssignments)) {
            yield put(setScreenData(['failedIdentityRoleAssignments'], failedIdentityRoleAssignments, screenKey));
            yield put(setScreenData(['isIdentityRolePartialFailure'], true, screenKey));
        }
    }
    if (!isEmpty(products)) {
        productsResult = yield call(PUT, `/clients/users/${id}/clients`, getHeaders('1.0'), products);
        if (productsResult && !productsResult.isSuccess) {
            errors.push({
                screenMessage: formatMessage(Locale.failedUpdateUserProducts),
                errorMessages: productsResult.errorMessages,
            });
        }
    }

    return errors;
}

function* addOrReplaceUser(users, userDetails) {
    const allUsers = users;
    const index = allUsers.findIndex(user => user.id === userDetails.id);

    if (index >= 0) {
        allUsers[index] = userDetails;
    } else {
        allUsers.push(userDetails);
    }

    yield put(setScreenData(['allUsers'], allUsers, ACTIVE_SCREEN_KEY));
}

export function* addUser(allUsers, userDetails, roles, connectedAppsData, authOnlyAppIds = []) {
    const updatedUserDetails = userDetails;

    updatedUserDetails.partnerId = getPartnerId();
    if (updatedUserDetails.contactInfo) {
        updatedUserDetails.additionalClaims = createAdditionalClaims(updatedUserDetails.contactInfo);
        delete updatedUserDetails.contactInfo;
    }

    if (updatedUserDetails?.mfaProviders?.includes('DuoAuthenticator'))
        createDuoAdditionalClaims(updatedUserDetails.additionalClaims, ADD_USER_DRAWER);

    const errors = [];
    const version = '3.0';
    const result = yield call(POST, '/users/', getHeaders(version), updatedUserDetails);
    const failedAppNames = [];
    if (result.isSuccess) {
        if (!isEmpty(connectedAppsData.connectedApps)) {
            const calls = map(connectedAppsData.connectedApps, app => {
                const payload = { ...app };
                delete payload.connectedAppId;
                return call(
                    PUT,
                    `/iambff/v1/users/${result.response.id}/connected-apps/${app.connectedAppId}/app-role-mappings`,
                    getHeaders(),
                    payload
                );
            });
            const apiRoleMappingResult = yield all(calls);
            if (!isEmpty(apiRoleMappingResult)) {
                const allConnectedApps =
                    getScreenData(Store().getState().screen, ['connectedApps'], ADD_USER_DRAWER)?.toJS() ?? [];
                forEach(apiRoleMappingResult, (roleMappingResult, index) => {
                    if (!roleMappingResult?.isSuccess) {
                        const connectedAppId = get(connectedAppsData.connectedApps[index], 'connectedAppId');
                        const appData = allConnectedApps.find(app => connectedAppId === app?.clientId);
                        failedAppNames.push(getAppDisplayName(appData));
                    }
                });
            }
        }

        yield call(updateUserProductsRoles, result.response.id, authOnlyAppIds, roles);

        updatedUserDetails.id = result.response.id;
        yield put(setScreenData(['userDetails'], Object.assign(updatedUserDetails, result.response), ADD_USER_DRAWER));

        yield call(addOrReplaceUser, allUsers, result.response);
    } else {
        yield put(hideDialog(UMS_LOADING_DIALOG));
        errors.push({
            screenMessage: formatMessage(Locale.failedCreateUser),
            errorMessages: result.errorMessages,
        });
    }

    if (errors.length > 0) {
        yield put({ type: 'ADD_USER_HAS_ERRORS', data: true });

        for (let i = 0; i < errors.length; i += 1) {
            yield put(setErrorScreenMessage(errors[i].screenMessage, false, errors[i].errorMessages, ADD_USER_DRAWER));
            yield call(autoHideMessages, ADD_USER_DRAWER);
        }
        if (result.isSuccess) {
            yield call(deleteUser, result.response.id);
            yield put(removeAllScreenMessages());
        }
    } else {
        yield put({ type: 'ADD_USER_HAS_ERRORS', data: false });
        if (isEmpty(failedAppNames)) {
            yield put(setSuccessfulScreenMessage(formatMessage(Locale.userAdded), false, []));
        } else {
            yield put(setScreenData(['isAddUserSuccessWithAppFailure'], true, ADD_USER_DRAWER));
            yield put(setScreenData(['failedAppNames'], failedAppNames.join(', '), ADD_USER_DRAWER));
        }
        yield call(autoHideMessages);
    }

    return result;
}

function parseErrors(result) {
    const errors = result.errorMessages ?? [];
    if (typeof result.error === 'string') errors.push(result.error);
    return errors;
}

export function* updateLoginProvider(userId, scheme, screenKey = ACTIVE_SCREEN_KEY) {
    if (scheme === null) {
        const result = yield call(
            POST,
            `/users/${userId}/RemoveLoginProvider`,
            getHeaders('2.0'),
            undefined,
            /* isJson = */ false
        );
        if (result.isSuccess) {
            if (screenKey !== EDIT_USER_DRAWER) {
                yield put(
                    setSuccessfulScreenMessage(
                        formatMessage(Locale.authenticationMethodAddedSuccessful),
                        false,
                        null,
                        screenKey
                    )
                );
            }
            return [];
        }
        yield put(setErrorScreenMessage(formatMessage(Locale.authenticationMethodAddedFailed), false, null, screenKey));
        yield call(autoHideMessages);

        return parseErrors(result);
    }

    const result = yield call(
        POST,
        `/users/${userId}/AssignLoginProvider`,
        getHeaders('2.0'),
        { scheme },
        /* isJson = */ false
    );
    return result.isSuccess ? [] : parseErrors(result);
}

export function* editUser(
    allUsers,
    id,
    userDetails,
    userDetailUpdates,
    isUserDisabled,
    products,
    roles,
    screenKey = ACTIVE_SCREEN_KEY,
    connectedAppsData,
    connectedAppsList
) {
    const errors = [];
    const version = '3.0';
    const failedConnectedApps = [];
    const updatedUserDetailUpdates = userDetailUpdates;
    if (updatedUserDetailUpdates.loginScheme) {
        if (
            !(
                (updatedUserDetailUpdates.loginScheme === 'Local Login' ||
                    updatedUserDetailUpdates.loginScheme.id === 'Local Login') &&
                userDetails.currentLoginScheme === 'Local Login'
            )
        ) {
            let scheme = updatedUserDetailUpdates.loginScheme.id;
            if (scheme === 'Local Login') scheme = null;
            const updateLoginProviderResult = yield call(updateLoginProvider, id, scheme, screenKey);
            if (updateLoginProviderResult.length !== 0) {
                const result = formatErrorMessage(updateLoginProviderResult);
                errors.push({
                    screenMessage: 'Error updating login provider',
                    errorMessages: result,
                });
            }
        }
        delete updatedUserDetailUpdates.loginScheme;
    }

    let additionalClaimsToRemove = [];
    updatedUserDetailUpdates.additionalClaims = [];
    if (updatedUserDetailUpdates.contactInfo) {
        if (userDetails) {
            updatedUserDetailUpdates.additionalClaims.push(
                ...updateContactInfoClaim(userDetails, updatedUserDetailUpdates.contactInfo)
            );
            additionalClaimsToRemove = getAdditionalClaimsToRemove(userDetails, userDetailUpdates.contactInfo);
        }
        delete updatedUserDetailUpdates.contactInfo;
    }

    const duoMfaUpdates = toArray(getScreenData(Store().getState().screen, ['duoMfaDetails'], screenKey));
    if (includes(get(userDetailUpdates, ['mfaProviders', 0, 1], []), 'DuoAuthenticator') && duoMfaUpdates) {
        const updatedDuoClaims = updateDuoClaims(userDetails, duoMfaUpdates);
        updatedUserDetailUpdates.additionalClaims.push(...updatedDuoClaims);
    }
    const duoMfaDisabled = getScreenUpdated(Store().getState().screen, ['mfaDetails', DUO_MFA], screenKey);
    if (duoMfaDisabled === false) {
        additionalClaimsToRemove.push(...duoClaimsToRemove(userDetails));
    }

    const newUserDetailUpdates = userDetailUpdates;
    // if no mfa is selected, set email mfa as default.
    if (isEmpty(userDetails.mfaProviders)) {
        if (isEmpty(userDetailUpdates.mfaProviders) || isEmpty(userDetailUpdates.mfaProviders[0][1])) {
            newUserDetailUpdates.mfaProviders = [['', ['Email']]];
        }
    }

    let userDetailsResult;
    if (Object.keys(newUserDetailUpdates).length !== 0) {
        // TODO: create a library or use a library that can building patches for us
        const patches = additionalClaimsToRemove.concat(convertMapToPatches(fromJS(newUserDetailUpdates)));
        userDetailsResult = yield call(PATCH, `/users/${id}`, getHeaders(version), patches, true);
    }
    const isInadequatePermissions = userDetailsResult.status === 403 || userDetailsResult.status === 401;
    if (userDetailsResult && !userDetailsResult.isSuccess && !isInadequatePermissions) {
        errors.push({
            screenMessage: formatMessage(Locale.failedUpdateUser),
            errorMessages: userDetailsResult.errorMessages,
        });
    } else if (userDetailsResult && (userDetailsResult.isSuccess || isInadequatePermissions)) {
        const connectedApps = get(connectedAppsData, 'connectedApps');
        const allConnectedApps = getScreenData(Store().getState().screen, ['connectedApps'], screenKey)?.toJS() ?? [];
        const initialRolesInfo =
            getScreenData(Store().getState().screen, ['assignedAppRolesInfo'], screenKey)?.toJS() ?? [];
        const initialRolesInfoIds = Object.keys(initialRolesInfo);
        const updatedData = getScreenUpdated(Store().getState().screen, [], screenKey)?.toJS() ?? [];
        const connectedAppsUpdatedData = get(updatedData, 'connectedApps') || {};
        const connectedAppsUpdatedResourcesData = get(updatedData, 'roleDetails.connectedApps') || {};
        const updatedConnectedAppsIdsForRoles = Object.keys(connectedAppsUpdatedData);
        const updatedConnectedAppsIdsForResources = Object.keys(connectedAppsUpdatedResourcesData);
        const updatedConnectedAppsIds = [
            ...new Set([...updatedConnectedAppsIdsForRoles, ...updatedConnectedAppsIdsForResources]),
        ];
        let appRoleMappingResult = '';
        const failedAppsInfo = [];
        for (let i = 0; i < size(connectedApps); i += 1) {
            const assignedRoles = { ...connectedApps[i] };
            const itemConnectedAppId = get(assignedRoles, 'connectedAppId');
            const resourceEnabledStatusData = [];
            const assignedRolesDataInfo = [];
            forEach(get(assignedRoles, 'connectedAppRoles'), assignedRolesData => {
                const itemRoleId = get(assignedRolesData, 'roleId');
                const resourceEnabledStatus = get(
                    connectedAppsList,
                    `${itemConnectedAppId}.rolesWithResources.${itemRoleId}.isResourcesEnabled`
                );
                resourceEnabledStatusData.push(resourceEnabledStatus);
                assignedRolesDataInfo.push(assignedRolesData.assignedResources);
            });

            const hasResourceDisabledItems = resourceEnabledStatusData.some(arr => arr === false);
            const hasCustomRolesAssigned = !isEmpty(assignedRolesDataInfo);
            const isConnectedAppsUpdated = find(updatedConnectedAppsIds, item => item === itemConnectedAppId);
            if ((hasResourceDisabledItems || hasCustomRolesAssigned) && isConnectedAppsUpdated) {
                delete assignedRoles.connectedAppId;
                appRoleMappingResult = yield call(
                    PUT,
                    `/iambff/v1/users/${id}/connected-apps/${itemConnectedAppId}/app-role-mappings`,
                    getHeaders(),
                    assignedRoles
                );

                if (!appRoleMappingResult.isSuccess) {
                    failedConnectedApps.push(itemConnectedAppId);
                    const app = allConnectedApps.find(item => itemConnectedAppId === item?.connectedAppId);
                    const errorString = getDisplayErrorText(get(appRoleMappingResult, 'error'), getAppDisplayName(app));
                    failedAppsInfo.push(errorString);
                }
            }
        }
        const connectedAppsAllIDs = connectedApps.map(item => item.connectedAppId);
        const removedRoles = updatedConnectedAppsIds.filter(element => !connectedAppsAllIDs.includes(element));
        const itemsToDelete = removedRoles.filter(element => initialRolesInfoIds.includes(element));

        if (size(itemsToDelete)) {
            for (let i = 0; i < size(itemsToDelete); i += 1) {
                appRoleMappingResult = yield call(
                    DELETE,
                    `/iambff/v1/users/${id}/connected-apps/${itemsToDelete[i]}/app-role-mappings`,
                    getHeaders()
                );
                if (!appRoleMappingResult.isSuccess) {
                    const app = allConnectedApps.find(item => itemsToDelete[i] === item?.connectedAppId);
                    const errorString = getDisplayErrorText(get(appRoleMappingResult, 'error'), getAppDisplayName(app));
                    failedAppsInfo.push(errorString);
                }
            }
        }

        if (appRoleMappingResult && !appRoleMappingResult.isSuccess) {
            const failedAppNames = [];

            forEach(failedConnectedApps, failedConnectedApp => {
                const appData = allConnectedApps.find(app => failedConnectedApp === app?.clientId);
                failedAppNames.push(getAppDisplayName(appData));
            });

            errors.push({
                screenMessage: `<b>${formatMessage(Locale.addUserAppAssignmentFailedHeader, {
                    appNames: failedAppNames.join(', '),
                })}</b> <br> ${formatMessage(Locale.addUserAppAssignmentFailed)}`,
                errorMessages: get(appRoleMappingResult, 'errorMessages'),
            });
        }

        if (!isInadequatePermissions) {
            const details = userDetailsResult.response;
            details.contactInfo = getUserContactInfoFromClaims(userDetails.additionalClaims);
            details.isContactEmailSameAsHome = details.email === details.contactInfo.contactEmail;
            if (userDetails.loginScheme === null) details.loginScheme = 'Local Login';
        }
        yield call(addOrReplaceUser, allUsers, userDetails);
    }

    yield call(updateUserProductsRoles, id, products, roles, EDIT_USER_DRAWER);
    const isIdentityRoleFailure = getScreenData(Store().getState().screen, ['isIdentityRoleFailure'], EDIT_USER_DRAWER);
    const isIdentityRolePartialFailure = getScreenData(
        Store().getState().screen,
        ['isIdentityRolePartialFailure'],
        EDIT_USER_DRAWER
    );
    if (isIdentityRolePartialFailure) {
        errors.push({
            screenMessage: `${formatMessage(Locale.addUsersIdentityAssignmentFailedHeader)}`,
            errorMessages: [formatMessage(Locale.identityRolesAssignmentFailedPartialorFull)],
        });
        yield call(reloadCurrentUsersPage);
    }
    if (isIdentityRoleFailure) {
        errors.push({
            screenMessage: `${formatMessage(Locale.addUsersIdentityAssignmentFailedHeader)}`,
            errorMessages: [formatMessage(Locale.identityRolesAssignmentFailedServerSide)],
        });
    }
    yield put(hideDialog(UMS_LOADING_DIALOG));
    if (errors.length > 0) {
        yield put({ type: 'EDIT_USER_HAS_ERRORS', data: true });
        for (let i = 0; i < errors.length; i += 1) {
            yield put(setErrorScreenMessage(errors[i].screenMessage, true, errors[i].errorMessages, EDIT_USER_DRAWER));
            yield call(autoHideMessagesAfter, 10000, EDIT_USER_DRAWER);
        }
    } else {
        yield put(hideDialog(UMS_LOADING_DIALOG));
        yield put({ type: 'EDIT_USER_HAS_ERRORS', data: false });
    }
    yield put(removeScreenData(['failedIdentityRoleAssignments'], EDIT_USER_DRAWER));
    yield put(removeScreenData(['isIdentityRoleFailure'], EDIT_USER_DRAWER));
    yield put(removeScreenData(['isIdentityRolePartialFailure'], EDIT_USER_DRAWER));
}

export function* resetPasswordUser(id, screenKey = ACTIVE_SCREEN_KEY) {
    const result = yield call(POST, `/users/${id}/resetpassword`, getHeaders(), {});
    yield put(hideDialog(UMS_LOADING_DIALOG));
    if (result.isSuccess) {
        yield put(setSuccessfulScreenMessage(formatMessage(Locale.resetPasswordSent), false, [], screenKey));
        yield put(
            setAlertScreenMessage(ALERT_TYPE.success, null, formatMessage(Locale.resetPasswordSent), true, screenKey)
        );
        yield call(autoHideMessages, screenKey);
    } else {
        const error = formatMessage(Locale.failedResetUserPassword);
        yield put(setErrorScreenMessage(error, false, result.errorMessages, screenKey));
        yield put(setAlertScreenMessage(ALERT_TYPE.error, null, error, true, screenKey));
        yield call(autoHideMessages, screenKey);
    }
}

export function* disableUsers(userIds, successMessage, screenKey = ACTIVE_SCREEN_KEY) {
    const result = yield call(POST, '/users/disableaccounts', getHeaders(), userIds);
    if (result.isSuccess) {
        const message = successMessage || formatMessage(Locale.disableUsersSuccess);
        yield put(setSuccessfulScreenMessage(message, false, [], screenKey));
        yield put(setAlertScreenMessage(ALERT_TYPE.success, null, message, true, screenKey));
        if (screenKey === EDIT_USER_DRAWER) yield call(getUser, userIds[0], screenKey);
        yield call(reloadCurrentUsersPage);
        yield call(autoHideMessages, screenKey);
    } else {
        yield put(hideDialog(UMS_LOADING_DIALOG));
        const error = formatMessage(Locale.failedDisableUser);
        yield put(setErrorScreenMessage(error, false, result.errorMessages));
        yield put(setAlertScreenMessage(ALERT_TYPE.error, null, result.errorMessages, true, screenKey));
        yield call(autoHideMessages, screenKey);
    }
}

export function* enableUsers(userIds, successMessage, screenKey = ACTIVE_SCREEN_KEY) {
    const result = yield call(POST, '/users/enableaccounts', getHeaders(), userIds);
    if (result.isSuccess) {
        const message = successMessage || formatMessage(Locale.enableUsersSuccess);
        yield put(setSuccessfulScreenMessage(message, false, [], screenKey));
        yield put(setAlertScreenMessage(ALERT_TYPE.success, null, message, true, screenKey));
        if (screenKey === EDIT_USER_DRAWER) yield call(getUser, userIds[0], screenKey);
        yield call(reloadCurrentUsersPage);
        yield call(autoHideMessages, screenKey);
    } else {
        yield put(hideDialog(UMS_LOADING_DIALOG));
        const error = formatMessage(Locale.failedEnableUser);
        yield put(setErrorScreenMessage(error, false, result.errorMessages));
        yield put(setAlertScreenMessage(ALERT_TYPE.error, null, result.errorMessages, true, screenKey));
        yield call(autoHideMessages, screenKey);
    }
}

export function* reinvitePendingInvite(selectedIds, screenKey = ACTIVE_SCREEN_KEY) {
    const result = yield call(POST, '/users/resendinvite', getHeaders(), selectedIds);
    if (result.isSuccess) {
        const failedUsersInvitation = filter(result.response, invitation => !invitation?.success);
        const successUsersInvitation = filter(result.response, invitation => invitation?.success);
        if (isEmpty(failedUsersInvitation)) {
            yield call(reloadCurrentUsersPage);
            yield put(setSuccessfulScreenMessage(formatMessage(Locale.invitationSent), false, [], screenKey));
            yield put(
                setAlertScreenMessage(ALERT_TYPE.success, null, formatMessage(Locale.invitationSent), true, screenKey)
            );
        } else if (isEmpty(successUsersInvitation)) {
            yield call(reloadCurrentUsersPage);
            yield put(setErrorScreenMessage(formatMessage(Locale.invitationSentFailed), false, [], screenKey));
            yield put(
                setAlertScreenMessage(
                    ALERT_TYPE.error,
                    null,
                    formatMessage(Locale.invitationSentFailed),
                    true,
                    screenKey
                )
            );
        } else {
            const usersList = toArray(getScreenData(Store().getState().screen, ['usersWithConnectedApps']));
            const failedUserEmails = map(failedUsersInvitation, invitation => {
                return find(usersList, users => users?.id === invitation?.id)?.email;
            });
            const passedUsersEmails = map(successUsersInvitation, invitation => {
                return find(usersList, users => users?.id === invitation?.id)?.email;
            });
            yield call(reloadCurrentUsersPage);

            yield put(
                setWarningScreenMessage(
                    `${formatMessage(Locale.invitationSent)} ${passedUsersEmails.join(', ')}
                    <br>
                     ${formatMessage(Locale.invitationSentFailed)} ${failedUserEmails.join(', ')}`,
                    true,
                    [],
                    screenKey
                )
            );
            yield put(
                setAlertScreenMessage(
                    ALERT_TYPE.warning,
                    null,
                    `${formatMessage(Locale.invitationSent)} ${passedUsersEmails.join(', ')}
                    <br>
                     ${formatMessage(Locale.invitationSentFailed)} ${failedUserEmails.join(', ')}`,
                    true,
                    screenKey
                )
            );
        }
        yield call(autoHideMessages, screenKey);
    } else {
        yield put(hideDialog(UMS_LOADING_DIALOG));
        const error = formatMessage(Locale.failedReinviteUser);
        yield put(setErrorScreenMessage(error, false, result.errorMessages, screenKey));
        yield call(autoHideMessages, screenKey);
    }
}

export function* disableTwoFactor(id, screenKey = ACTIVE_SCREEN_KEY) {
    const result = yield call(POST, `/users/${id}/disableTwoFactor`, getHeaders('2.0'), {}, /* isJson = */ false);
    if (result.isSuccess) {
        yield call(reloadCurrentUsersPage);
        yield put(setSuccessfulScreenMessage(formatMessage(Locale.MFAForgotten), false, [], screenKey));
        yield call(autoHideMessages);
    } else {
        const error = formatMessage(Locale.failedForgetMFA);
        yield put(setErrorScreenMessage(error, false, result.errorMessages, screenKey));
        yield call(autoHideMessages);
    }
}

export function* getValidMfaProviders(userId, screenKey = ACTIVE_SCREEN_KEY) {
    const result = yield call(GET, `/users/admin/${userId}/mfa/GetValid2faProviders`, getHeaders('2.0'));
    if (result.isSuccess) {
        yield put(setScreenData(['validProviders'], result.response.validProviders));
    } else {
        const error = formatMessage(Locale.failedToGetValidMfaProvidersMessage);
        yield put(setErrorScreenMessage(error, false, result.errorMessages, screenKey));
        yield call(autoHideMessages);
    }
}

export function* disableEmailMfa(userId, screenKey = ACTIVE_SCREEN_KEY) {
    const result = yield call(POST, `/users/admin/${userId}/mfa/DisableEmail2fa`, getHeaders('2.0'));
    if (result.isSuccess) {
        yield call(reloadCurrentUsersPage);
        yield put(setSuccessfulScreenMessage(formatMessage(Locale.emailMfaForgotten), false, [], screenKey));
        yield call(autoHideMessages);
    } else {
        const error = formatMessage(Locale.failedToDisableEmailMfa);
        yield put(setErrorScreenMessage(error, false, result.errorMessages, screenKey));
        yield call(autoHideMessages);
    }
}

export function* disableAuthAppMfa(userId, screenKey = ACTIVE_SCREEN_KEY) {
    const result = yield call(POST, `/users/admin/${userId}/mfa/RemoveAuthenticatorKey`, getHeaders('3.0'));
    if (result.isSuccess) {
        yield call(reloadCurrentUsersPage);
        yield put(setSuccessfulScreenMessage(formatMessage(Locale.authAppMfaForgotten), false, [], screenKey));
        yield call(autoHideMessages);
    } else {
        const error = formatMessage(Locale.failedToDisableAuthAppMfa);
        yield put(setErrorScreenMessage(error, false, result.errorMessages, screenKey));
        yield call(autoHideMessages);
    }
}

export function* getPartnerOptinStatus(screenKey = ACTIVE_SCREEN_KEY) {
    yield put(setScreenData(['isPartnerOptInCheckLoading'], true, screenKey));
    const result = yield call(GET, '/iambff/v1/connected-apps/asio_platform/opt-ins', getHeaders());

    if (result.isSuccess) {
        yield put(setScreenData(['partnerOptInStatus'], result.response, screenKey));
    }

    yield put(setScreenData(['isPartnerOptInCheckLoading'], false, screenKey));
}

export const isUserAdmin = userRoles => !isEmpty(userRoles) && userRoles.includes(DEFAULT_ROLES.Admin);

export function* editGuestUser(
    id,
    products,
    roles,
    screenKey = ACTIVE_SCREEN_KEY,
    connectedAppsData,
    connectedAppsList
) {
    const errors = [];
    const originPartnerId = getOriginPartnerId();
    // Commented for vodafone use case, might require in future. Ref ticket IAM-10875.
    // const version = '3.0';
    const failedConnectedApps = [];
    // Commented for vodafone use case, might require in future. Ref ticket IAM-10875.
    // const updatedUserDetailUpdates = userDetailUpdates;
    // if (updatedUserDetailUpdates.loginScheme) {
    //     if (
    //         !(
    //             (updatedUserDetailUpdates.loginScheme === 'Local Login' ||
    //                 updatedUserDetailUpdates.loginScheme.id === 'Local Login') &&
    //             userDetails.currentLoginScheme === 'Local Login'
    //         )
    //     ) {
    //         let scheme = updatedUserDetailUpdates.loginScheme.id;
    //         if (scheme === 'Local Login') scheme = null;
    //         const updateLoginProviderResult = yield call(updateLoginProvider, id, scheme, screenKey);
    //         if (updateLoginProviderResult.length !== 0) {
    //             const result = formatErrorMessage(updateLoginProviderResult);
    //             errors.push({
    //                 screenMessage: 'Error updating login provider',
    //                 errorMessages: result,
    //             });
    //         }
    //     }
    //     delete updatedUserDetailUpdates.loginScheme;
    // }

    // let additionalClaimsToRemove = [];
    // updatedUserDetailUpdates.additionalClaims = [];
    // if (updatedUserDetailUpdates.contactInfo) {
    //     if (userDetails) {
    //         updatedUserDetailUpdates.additionalClaims.push(
    //             ...updateContactInfoClaim(userDetails, updatedUserDetailUpdates.contactInfo)
    //         );
    //         additionalClaimsToRemove = getAdditionalClaimsToRemove(userDetails, userDetailUpdates.contactInfo);
    //     }
    //     delete updatedUserDetailUpdates.contactInfo;
    // }

    // const duoMfaUpdates = toArray(getScreenData(Store().getState().screen, ['duoMfaDetails'], screenKey));
    // if (includes(get(userDetailUpdates, ['mfaProviders', 0, 1], []), 'DuoAuthenticator') && duoMfaUpdates) {
    //     const updatedDuoClaims = updateDuoClaims(userDetails, duoMfaUpdates);
    //     updatedUserDetailUpdates.additionalClaims.push(...updatedDuoClaims);
    // }
    // const duoMfaDisabled = getScreenUpdated(Store().getState().screen, ['mfaDetails', DUO_MFA], screenKey);
    // if (duoMfaDisabled === false) {
    //     additionalClaimsToRemove.push(...duoClaimsToRemove(userDetails));
    // }

    // const newUserDetailUpdates = userDetailUpdates;
    // // if no mfa is selected, set email mfa as default.
    // if (isEmpty(userDetails.mfaProviders)) {
    //     if (isEmpty(userDetailUpdates.mfaProviders) || isEmpty(userDetailUpdates.mfaProviders[0][1])) {
    //         newUserDetailUpdates.mfaProviders = [['', ['Email']]];
    //     }
    // }

    // let userDetailsResult;
    // if (Object.keys(newUserDetailUpdates).length !== 0) {
    //     // TODO: create a library or use a library that can building patches for us
    //     const patches = additionalClaimsToRemove.concat(convertMapToPatches(fromJS(newUserDetailUpdates)));
    //     userDetailsResult = yield call(PATCH, `/users/${id}`, getHeaders(version), patches, true);
    // }

    // if (userDetailsResult && !userDetailsResult.isSuccess) {
    //     errors.push({
    //         screenMessage: formatMessage(Locale.failedUpdateUser),
    //         errorMessages: userDetailsResult.errorMessages,
    //     });
    // } else
    if (id) {
        const connectedApps = get(connectedAppsData, 'connectedApps');
        const allConnectedApps = getScreenData(Store().getState().screen, ['connectedApps'], screenKey)?.toJS() ?? [];
        const initialRolesInfo =
            getScreenData(Store().getState().screen, ['assignedAppRolesInfo'], screenKey)?.toJS() ?? [];
        const initialRolesInfoIds = Object.keys(initialRolesInfo);
        const updatedData = getScreenUpdated(Store().getState().screen, [], screenKey)?.toJS() ?? [];
        const connectedAppsUpdatedData = get(updatedData, 'connectedApps') || {};
        const connectedAppsUpdatedResourcesData = get(updatedData, 'roleDetails.connectedApps') || {};
        const updatedConnectedAppsIdsForRoles = Object.keys(connectedAppsUpdatedData);
        const updatedConnectedAppsIdsForResources = Object.keys(connectedAppsUpdatedResourcesData);
        const updatedConnectedAppsIds = [
            ...new Set([...updatedConnectedAppsIdsForRoles, ...updatedConnectedAppsIdsForResources]),
        ];
        let appRoleMappingResult = '';
        const failedAppsInfo = [];
        for (let i = 0; i < size(connectedApps); i += 1) {
            const assignedRoles = { ...connectedApps[i] };
            const itemConnectedAppId = get(assignedRoles, 'connectedAppId');
            const resourceEnabledStatusData = [];
            const assignedRolesDataInfo = [];
            forEach(get(assignedRoles, 'connectedAppRoles'), assignedRolesData => {
                const itemRoleId = get(assignedRolesData, 'roleId');
                const resourceEnabledStatus = get(
                    connectedAppsList,
                    `${itemConnectedAppId}.rolesWithResources.${itemRoleId}.isResourcesEnabled`
                );
                resourceEnabledStatusData.push(resourceEnabledStatus);
                assignedRolesDataInfo.push(assignedRolesData.assignedResources);
            });

            const hasResourceDisabledItems = resourceEnabledStatusData.some(arr => arr === false);
            const hasCustomRolesAssigned = !isEmpty(assignedRolesDataInfo);
            const isConnectedAppsUpdated = find(updatedConnectedAppsIds, item => item === itemConnectedAppId);
            if ((hasResourceDisabledItems || hasCustomRolesAssigned) && isConnectedAppsUpdated) {
                delete assignedRoles.connectedAppId;
                appRoleMappingResult = yield call(
                    PUT,
                    `/iambff/v1/users/${id}/connected-apps/${itemConnectedAppId}/app-role-mappings?originPartnerId=${originPartnerId}`,
                    getHeaders(),
                    assignedRoles
                );

                if (!appRoleMappingResult.isSuccess) {
                    failedConnectedApps.push(itemConnectedAppId);
                    const app = allConnectedApps.find(item => itemConnectedAppId === item?.connectedAppId);
                    const errorString = getDisplayErrorText(get(appRoleMappingResult, 'error'), getAppDisplayName(app));
                    failedAppsInfo.push(errorString);
                }
            }
        }
        const connectedAppsAllIDs = connectedApps.map(item => item.connectedAppId);
        const removedRoles = updatedConnectedAppsIds.filter(element => !connectedAppsAllIDs.includes(element));
        const itemsToDelete = removedRoles.filter(element => initialRolesInfoIds.includes(element));

        if (size(itemsToDelete)) {
            for (let i = 0; i < size(itemsToDelete); i += 1) {
                appRoleMappingResult = yield call(
                    DELETE,
                    `/iambff/v1/users/${id}/connected-apps/${itemsToDelete[i]}/app-role-mappings?originPartnerId=${originPartnerId}`,
                    getHeaders()
                );
                if (!appRoleMappingResult.isSuccess) {
                    const app = allConnectedApps.find(item => itemsToDelete[i] === item?.connectedAppId);
                    const errorString = getDisplayErrorText(get(appRoleMappingResult, 'error'), getAppDisplayName(app));
                    failedAppsInfo.push(errorString);
                }
            }
        }

        if (appRoleMappingResult && !appRoleMappingResult.isSuccess) {
            const failedAppNames = [];

            forEach(failedConnectedApps, failedConnectedApp => {
                const appData = allConnectedApps.find(app => failedConnectedApp === app?.clientId);
                failedAppNames.push(getAppDisplayName(appData));
            });

            errors.push({
                screenMessage: `<b>${formatMessage(Locale.addUserAppAssignmentFailedHeader, {
                    appNames: failedAppNames.join(', '),
                })}</b> <br> ${formatMessage(Locale.addUserAppAssignmentFailed)}`,
                errorMessages: get(appRoleMappingResult, 'errorMessages'),
            });
        }

        // Commented for vodafone use case, might require in future. Ref ticket IAM-10875.
        // const details = userDetailsResult.response;
        // details.contactInfo = getUserContactInfoFromClaims(userDetails.additionalClaims);
        // details.isContactEmailSameAsHome = details.email === details.contactInfo.contactEmail;
        // if (userDetails.loginScheme === null) details.loginScheme = 'Local Login';
    }

    yield call(updateUserProductsRoles, id, products, roles, screenKey);
    const isIdentityRoleFailure = getScreenData(Store().getState().screen, ['isIdentityRoleFailure'], screenKey);
    const isIdentityRolePartialFailure = getScreenData(
        Store().getState().screen,
        ['isIdentityRolePartialFailure'],
        screenKey
    );
    if (isIdentityRolePartialFailure) {
        errors.push({
            screenMessage: `${formatMessage(Locale.addUsersIdentityAssignmentFailedHeader)}`,
            errorMessages: [formatMessage(Locale.identityRolesAssignmentFailedPartialorFull)],
        });
        yield call(reloadCurrentUsersPage, ACTIVE_SCREEN_KEY, false, true);
    }
    if (isIdentityRoleFailure) {
        errors.push({
            screenMessage: `${formatMessage(Locale.addUsersIdentityAssignmentFailedHeader)}`,
            errorMessages: [formatMessage(Locale.identityRolesAssignmentFailedServerSide)],
        });
    }
    yield put(hideDialog(UMS_LOADING_DIALOG));
    if (errors.length > 0) {
        for (let i = 0; i < errors.length; i += 1) {
            yield put(setErrorScreenMessage(errors[i].screenMessage, true, errors[i].errorMessages, screenKey));
            yield call(autoHideMessagesAfter, 10000, screenKey);
        }
    } else {
        yield put(hideDialog(UMS_LOADING_DIALOG));
    }
    yield put(removeScreenData(['failedIdentityRoleAssignments'], screenKey));
    yield put(removeScreenData(['isIdentityRoleFailure'], screenKey));
    yield put(removeScreenData(['isIdentityRolePartialFailure'], screenKey));
}

export default loadAllUsers;

export function* updateUsersAppRole(connectedAppId, appRoleId, selectedUserIDs, screenKey = ACTIVE_SCREEN_KEY) {
    yield put(setScreenData(['isReady'], false, screenKey));
    yield put(setScreenData(['isGuestUsersReady'], false, screenKey));
    yield put(showDialog(DEFAULT_LOADING_DIALOG));
    const body = {
        userIds: selectedUserIDs,
    };

    const result = yield call(
        PUT,
        `/iambff/v1/connected-apps/${connectedAppId}/app-roles/${appRoleId}/app-role-mappings`,
        getHeaders(),
        body
    );
    yield put(setScreenData(['isReady'], true, screenKey));
    yield put(setScreenData(['isGuestUsersReady'], true, screenKey));
    yield put(hideDialog(DEFAULT_LOADING_DIALOG));

    if (result.isSuccess) {
        if (!result.response.userIds) {
            yield put(
                setAlertScreenMessage(ALERT_TYPE.success, null, formatMessage(Locale.userUpdated), true, screenKey)
            );
        } else {
            yield put(setAlertScreenMessage(ALERT_TYPE.warning, false, result.error));
        }
        yield put(setScreenData(['fetchedUsers'], selectedUserIDs));
    } else {
        yield put(setAlertScreenMessage(ALERT_TYPE.error, false, result.error));
        const fetchedUsers = getScreenData(Store().getState().screen, ['fetchedUsers']);
        yield put(setScreenData(['selectedUsers'], fetchedUsers));
    }
}
